import { Link, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useLocalStorage } from 'usehooks-ts';

import { setApplicationTypeRequest } from '@/api/operation';

import { BrowserDetectorIcon } from '@/components/BrowserDetector/BrowserDetector.styled';

import { LS_USER_BLOCKED_AT_PATH } from '@/constants/common';
import { ROUTE } from '@/constants/routes';

import { useConfirmation } from '@/context';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useGetCurrentUser } from '@/hooks/useGetCurrentUser';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { fetchAccountTypes } from '@/redux/user-data/actions';

import { ApplicationType } from '@/types/user';

export default function useHandleApplicationType() {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const { showConfirmation } = useConfirmation();
  const user = useAppSelector(selectLoggedInUser);

  const [_, setUserBlockedPath] = useLocalStorage(LS_USER_BLOCKED_AT_PATH, '');
  const navigate = useNavigate();

  const { refetch: refetchCurrentUser } = useGetCurrentUser();

  const { mutate, isPending } = useMutation({
    mutationKey: ['setApplicationType'],
    mutationFn: setApplicationTypeRequest,
    onSettled: () => {
      refetchCurrentUser();
    },
    onSuccess: () => {
      dispatch(fetchAccountTypes());
    },
  });

  const handleSelectApplicationType = (
    appType: ApplicationType | 'BUSINESS',
    isComingSoon: boolean = false,
  ) => {
    if (appType === ApplicationType.JOINT && isComingSoon) {
      navigate(ROUTE.RIGHT_START_COMING_SOON, {
        state: { notNavigateByStatus: true },
      });
      return;
    }

    if (appType === ApplicationType.BUSINESS && isComingSoon) {
      navigate(ROUTE.BUSINESS_COMING_SOON);
      return;
    }
    if (appType === ApplicationType.SHARED_WITH_SOMEONE_ELSE && isComingSoon) {
      navigate(ROUTE.SHARED_WITH_SOMEONE_ELSE_COMING_SOON);
      return;
    }

    if (appType === ApplicationType.OTHER) {
      return showConfirmation({
        showHeader: false,
        drawerImage: <BrowserDetectorIcon />,
        content: <ModalContent />,
        primaryText: t('modalConfirmPersonalUseOnly.primaryText'),
        secondaryText: t('modalConfirmPersonalUseOnly.secondaryText'),
        onConfirm: () => {
          mutate({
            operationId: user?.operationId,
            applicationType: appType,
          });
          setUserBlockedPath(ROUTE.APPLICATION_TYPE_SELECTION_FAILED);
        },
        onCancel: () => {
          console.log('User cancelled');
        },
      });
    }
    mutate({
      operationId: user?.operationId,
      applicationType: appType,
    });
  };

  return {
    handleSelectApplicationType,
    isPending,
  };
}

function ModalContent() {
  const { t, language } = useTranslate();
  return (
    <Stack
      spacing={1}
      direction="column"
      alignItems="center"
      textAlign="center"
      mb={2}
    >
      <Typography mb={2} variant="h3">
        {t('applicationType.modalConfirmPersonalUseOnly.header')}
      </Typography>
      <Typography variant="body2">
        <Trans
          i18nKey="applicationType.modalConfirmPersonalUseOnly.description"
          lang={language}
          as="div"
          components={{
            a1: (
              <Link
                href="https://republictt.com/about/branches-abms"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textDecoration: 'none',
                  fontWeight: 'medium',
                }}
              />
            ),
          }}
        />
      </Typography>
    </Stack>
  );
}
